/**
 * URL del flujo de registro e ingreso (C02 y C03). Vive en parámetros de la landing para funcionar
 * con "atrás" y poder enlazarse desde cualquier pantalla con `links.login(next)`:
 * `/?registro=1` (número y correo) · `/?registro=verificar` (código) · `&next=/ruta/interna`.
 */
export const FLOW_PARAM = 'registro';
export const NEXT_PARAM = 'next';
/** `&caduco=1`: la sesión caducó; el primer paso lo dice en vez de dar la bienvenida. */
export const EXPIRED_PARAM = 'caduco';
/** `?origen=qr`: llegó por el QR impreso de registro (suena la melodía de bienvenida al entrar). */
export const ORIGIN_PARAM = 'origen';
export const QR_ORIGIN = 'qr';

export type FlowStep = 'datos' | 'codigo';

const STEP_VALUE: Record<FlowStep, string> = { datos: '1', codigo: 'verificar' };

/** Paso abierto según la URL, o `null` si el flujo está cerrado. Cualquier otro valor abre el paso 1. */
export function flowStepOf(params: URLSearchParams): FlowStep | null {
  const value = params.get(FLOW_PARAM);
  if (value === null) return null;
  return value === STEP_VALUE.codigo ? 'codigo' : 'datos';
}

/** Parámetros de un paso conservando `next` (ya validado). */
export function flowSearch(step: FlowStep, next: string | null): string {
  const params = new URLSearchParams({ [FLOW_PARAM]: STEP_VALUE[step] });
  if (next) params.set(NEXT_PARAM, next);
  return `?${params.toString()}`;
}

/** Los parámetros actuales sin los del flujo (al cerrarlo). */
export function withoutFlow(params: URLSearchParams): string {
  const rest = new URLSearchParams(params);
  rest.delete(FLOW_PARAM);
  rest.delete(NEXT_PARAM);
  rest.delete(EXPIRED_PARAM);
  const text = rest.toString();
  return text ? `?${text}` : '';
}

// Caracteres de control: un salto de línea o tabulador puede colar "//" o "/\" en algunos parsers
const CONTROL = /[\u0000-\u001f\u007f]/;

/**
 * A dónde volver al terminar: solo rutas internas que empiecen por "/" (nunca "//otro.sitio",
 * "/\\otro.sitio", "https://…" ni "javascript:…") y que no sean el propio registro.
 */
export function safeNext(raw: string | null | undefined, origin?: string): string | null {
  if (!raw) return null;
  const value = raw.trim();
  if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\') || CONTROL.test(value)) return null;
  const base = origin ?? (typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
  let url: URL;
  try {
    url = new URL(value, base);
  } catch {
    return null;
  }
  if (url.origin !== new URL(base).origin) return null;
  if (url.searchParams.has(FLOW_PARAM)) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}
